/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * RNG seam. Production code uses `getRandomBytes` (or `defaultRandom`),
 * which delegates to `crypto.getRandomValues`. Tests can replace the seam
 * via `vi.mock('./random.js', ...)` and inject `seededRandom(seed)` for
 * reproducible byte streams.
 */

import { chacha20 } from '@noble/ciphers/chacha.js';

export interface Random {
  getRandomBytes(n: number): Uint8Array;
}

const BROWSER_RNG_QUOTA_BYTES = 65536;

export const defaultRandom: Random = {
  getRandomBytes(n: number): Uint8Array {
    const out = new Uint8Array(n);
    for (let offset = 0; offset < n; offset += BROWSER_RNG_QUOTA_BYTES) {
      const end = Math.min(offset + BROWSER_RNG_QUOTA_BYTES, n);
      crypto.getRandomValues(out.subarray(offset, end));
    }
    return out;
  },
};

export const getRandomBytes = (n: number): Uint8Array =>
  defaultRandom.getRandomBytes(n);

const SEED_LENGTH_BYTES = 32;
const NONCE_LENGTH_BYTES = 12;
const REFILL_BLOCK_BYTES = 4096;

/**
 * Produces a deterministic, test-only Random that emits a ChaCha20 keystream derived from the provided 32-byte seed and an incrementing 96-bit nonce.
 *
 * @param seed - A 32-byte seed used as the ChaCha20 key.
 * @returns A Random whose `getRandomBytes(n)` returns `n` deterministic pseudorandom bytes; two instances created with the same seed produce identical byte streams for the same consumption pattern.
 * @throws Error if `seed` length is not 32 bytes.
 * @throws Error if the 96-bit nonce wraps after exhausting its space.
 */
export function seededRandom(seed: Uint8Array): Random {
  if (seed.length !== SEED_LENGTH_BYTES) {
    throw new Error(
      `seededRandom requires a ${SEED_LENGTH_BYTES}-byte seed, got ${seed.length}`
    );
  }
  const key = new Uint8Array(seed);
  const nonce = new Uint8Array(NONCE_LENGTH_BYTES);
  let buffer = new Uint8Array(0);
  let offset = 0;

  const incrementNonce = (): void => {
    for (let i = 0; i < NONCE_LENGTH_BYTES; i++) {
      nonce[i] = ((nonce[i] ?? 0) + 1) & 0xff;
      if (nonce[i] !== 0) return;
    }
    throw new Error('seededRandom nonce wrapped (consumed >2^96 blocks)');
  };

  const refill = (): void => {
    buffer = chacha20(key, nonce, new Uint8Array(REFILL_BLOCK_BYTES));
    offset = 0;
    incrementNonce();
  };

  return {
    getRandomBytes(n: number): Uint8Array {
      const out = new Uint8Array(n);
      let written = 0;
      while (written < n) {
        if (offset >= buffer.length) refill();
        const take = Math.min(n - written, buffer.length - offset);
        out.set(buffer.subarray(offset, offset + take), written);
        offset += take;
        written += take;
      }
      return out;
    },
  };
}
